// --- BASIC ---
import { LitElement, html, property, customElement } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { connect } from 'pwa-helpers';
import { Router } from '@vaadin/router';
import { translate } from '@appnest/lit-translate';
import { getFlagForLang } from '../components/i18n';
import { Kode4Growl } from '@kode4/ui';
import { store, AppState } from '../store';
import { setLang, alertMessages, loadUserProfile } from '../store/ui/thunks';
import {
  checkAuthenticated,
  checkRole,
} from '../../packages/identity/dist/redux/guards';
import { signout } from '../../packages/identity/dist/redux/thunks';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { init } from '../../packages/identity/dist/redux/thunks';
import { initI18n } from '../store/ui/thunks';
import {
  authSignInRoute,
  authSignOutRoute,
  authDefaultRedirectAfterSignInRoute,
} from '../../packages/identity/dist/redux/actions';
import { initRouter } from '../router';
import { trackEvent } from '../utils/gtag';

@customElement('paramaster-page-layout')
export default class ParamasterPageLayout extends connect(store)(LitElement) {
  @property()
  private initialized: boolean = false;

  @property()
  private initializedHiding: boolean = false;

  @property()
  private language: string = '';

  @property()
  private languages: Array<string> = [];

  @property()
  private logoLang: string =
    navigator.language || (navigator as any).userLanguage || 'en';

  @property()
  private availableLogos: string[] = ['de', 'en', 'es', 'fr', 'it', 'zh'];

  @property()
  public growl: Kode4Growl | null = null;

  @property()
  private dialogLocaleVisible: boolean = false;

  @property()
  private h1Title: string = '';

  @property()
  private subTitle: string = '';

  @property()
  private headerClass: string = '';

  @property()
  private footerClass: string = '';

  @property()
  private bodyClass: string = '';

  @property()
  private username: string = '';

  @property()
  private isAuthenticated: boolean = false;

  @property()
  private hasAdminAccess: boolean = false;

  @property()
  private hasSuperuserAccess: boolean = false;

  @property()
  private busy: boolean = false;

  public firstUpdated() {
    this.growl = this.querySelector('kode4-growl');
  }

  public async connectedCallback() {
    store.dispatch(authSignInRoute('/welcome'));
    store.dispatch(authSignOutRoute('/welcome'));
    store.dispatch(authDefaultRedirectAfterSignInRoute('/paramaster'));
    const routerAdapter = {
      go(route: string) {
        Router.go(route);
      },
    };

    init(store, routerAdapter);

    store.dispatch(<any>initI18n());
    initRouter();

    super.connectedCallback();
  }

  async stateChanged(state: AppState) {
    this.busy = state.ui.busy > 0 || state.paramentor.busy > 0;

    if (
      state.auth?.decodedAccessToken?.uid &&
      state.auth?.decodedAccessToken?.uid !== state.ui.user?.id
    ) {
      const user = await store.dispatch(
        <any>loadUserProfile(state.auth.decodedAccessToken.uid, true, true),
      );
    }

    if (state.auth.initialized) {
      checkAuthenticated({ suppressError: true }).then(
        (result) => {
          this.isAuthenticated = result;
        },
        (err) => {
          this.isAuthenticated = false;
        },
      );

      checkRole(['paramaster_admin'], { suppressError: true }).then(
        (result) => {
          this.hasAdminAccess = result;
        },
        (err) => {
          this.hasAdminAccess = false;
        },
      );

      checkRole(['superuser'], { suppressError: true }).then(
        (result) => {
          this.hasSuperuserAccess = result;
        },
        (err) => {
          this.hasSuperuserAccess = false;
        },
      );
    }
    this.username = state.auth.decodedAccessToken?.name || '';

    this.h1Title = state.ui.title;
    this.subTitle = state.ui.subTitle;
    this.headerClass = state.ui.headerClass;
    this.footerClass = state.ui.footerClass;
    this.bodyClass = state.ui.bodyClass;
    this.language = state.ui.lang;
    this.languages = state.ui.langs;
    this.logoLang = state.ui.logoLang;

    if (!this.availableLogos.includes(this.logoLang)) {
      this.logoLang = '';
    }

    if (
      !this.initialized &&
      !this.initializedHiding &&
      state.ui.i18nInitialized
    ) {
      this.initializedHiding = true;
      setTimeout(() => {
        this.initialized = true;
        this.initializedHiding = false;
      }, 250);
    }

    if (this.growl) {
      store.dispatch(<any>alertMessages(this.growl));
    }
  }

  render() {
    return html`
            ${
              !this.initialized || this.initializedHiding
                ? html`
                    <div
                      class="${classMap({
                        paramasterInitializing:
                          !this.initialized && !this.initializedHiding,
                        paramasterInitializingFadeout: this.initializedHiding,
                      })}"
                    >
                      <div>
                        <img
                          src="assets/wikus-logo.png"
                          alt="Wikus"
                          style="height: 100px"
                        />
                        <div style="height: 50px;"></div>
                        <kode4-progress
                          type="icon"
                          color="primary"
                          indeterminate
                        ></kode4-progress>
                      </div>
                    </div>
                  `
                : ''
            }

            ${
              this.busy
                ? html`
                    <div
                      style="position: fixed; left: 0; right: 0; bottom: 0; height: 4px; z-index: 2000;"
                    >
                      <kode4-progress
                        color="primary"
                        indeterminate
                        style="height: 4px; opacity: 0.5"
                      ></kode4-progress>
                    </div>
                  `
                : ''
            }

            <header class="${this.headerClass}">
                <nav class="header-meta-nav">
                    ${
                      this.language === 'de'
                        ? html`
                            <a href="https://www.wikus-karriere.de/"
                              >${translate(
                                'paramaster.web.link.wikuskarriere.label',
                              )}</a
                            >
                          `
                        : ''
                    }
                    <a href="https://www.wikus.de/de/partnerportal">${translate('paramaster.web.link.partnerportal.label')}</a>
                        <a href="https://www.wikus.de/de/unternehmen/wikademy">${translate('paramaster.web.link.wikademy.label')}</a>
                    <a href="https://www.wikus.de">${translate('paramaster.web.link.wikuswebsite.label')}</a>
                    <span class="separator"></span>
                    <a class="selected-locale" @click="${(e: MouseEvent) => {
                      e.preventDefault();
                      this.dialogLocaleVisible = true;
                    }}">
                        <img src="${getFlagForLang(this.language)}">
                    </a>
                </nav>

                <div class="header-main-nav">
                    <a href="/" class="logo">
                        <img style="height: 63px" src="assets/wikus-logo${this.initialized && this.logoLang ? `-${this.logoLang}` : '-en'}.png" alt="Wikus">
                    </a>

                    <nav class="main-nav">
                        ${!this.isAuthenticated ? html`<a href="/">Home</a>` : ''}
                        ${!this.isAuthenticated ? html`<a href="/registrieren">${translate('signup.button.signuo')}</a>` : ''}
                        ${this.isAuthenticated ? html`<a href="/paramaster">${translate('webapp.title.mini')}</a>` : ''}
                        ${this.hasAdminAccess ? html`<a href="/users">Benutzer</a>` : ''}
                        ${false ? html`<a href="/translations">Übersetzungen</a>` : ''}
                        ${this.hasSuperuserAccess ? html`<a href="/datamanager">Daten</a>` : ''}
                        ${this.isAuthenticated ? html`<a href="/einstellungen">${translate('usersettings.title')}</a>` : ''}
                        ${
                          this.isAuthenticated
                            ? html`<a
                                href="/"
                                @click="${(e: MouseEvent) => {
                                  e.preventDefault();
                                  trackEvent('logout');
                                  store.dispatch(<any>signout());
                                }}"
                                >${translate('signout')}</a
                              >`
                            : ''
                        }
                    </nav>

                    <div class="auth">
                        ${
                          this.isAuthenticated
                            ? html`
                                <a href="/einstellungen">
                                  ${this.username} &nbsp;
                                  <kode4-fa-icon
                                    .icon="${faUser}"
                                  ></kode4-fa-icon>
                                </a>
                              `
                            : html`
                                <a href="/#anmelden"
                                  >${translate('signin.button')}</a
                                >
                              `
                        }
                    </a>
                    </div>
                </div>

                <div class="header-content">
                    <h1>
                        ${translate(this.h1Title)}
                        ${this.subTitle ? html`<br />${translate(this.subTitle)}` : ''}
                    </h1>
                </div>
            </header>

            <div class="dialog-locale ${this.dialogLocaleVisible ? 'dialog-locale-visible' : ''}">
                <div class="dialog-locale-content">
                    <div class="dialog-locale-back">
                        <kode4-button
                            primary
                            xs
                            icon
                            outline
                            flat
                            @click="${(e: MouseEvent) => {
                              e.preventDefault();
                              this.dialogLocaleVisible = false;
                            }}">
                            <kode4-fa-icon .icon="${faArrowLeft}"></kode4-fa-icon>
                        </kode4-button>
                        <kode4-button
                            primary
                            s
                            flat
                            @click="${(e: MouseEvent) => {
                              e.preventDefault();
                              this.dialogLocaleVisible = false;
                            }}">
                            ${translate('signup.button.back')}
                        </kode4-button>
                    </div>

                    <div class="dialog-locale-header">
                        ${translate('commons.account.language')}
                    </div>

                    <div class="dialog-locale-locales">
                        ${this.languages.map((lang) => this.renderLocaleLink(lang))}
                    </div>
                </div>
            </div>

            <div class="main ${this.bodyClass}">
                <main></main>
            </div>

            ${this.renderFooter()}

            <kode4-growl></kode4-growl>
        `;
  }

  renderFooter() {
    return html`
      <footer class="footer hide-grey-bar">
        <div class="footer__top">
          <div class="container">
            <div>
              <h3>${translate('web.footer.contact.title')}</h3>
              <p>${translate('web.footer.contact.text')}</p>
            </div>
            <div>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="mailto:paramaster@wikus.de"
                >${translate('web.footer.contact.button')}</a
              >
            </div>
          </div>
        </div>

        <div class="footer__bottom">
          <div class="container">
            <div>
              <h3>Paramaster® App</h3>
              <a
                href="https://itunes.apple.com/de/app/paramaster/id1309490908"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="assets/appstore.png"
                  alt="App Store"
                  title="App Store"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=de.wikus.paramaster"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="assets/playstore.png"
                  alt="Google Play"
                  title="Google Play"
                />
              </a>
            </div>
            <div>
              <h3>Folgen Sie uns</h3>
              <a
                href="https://www.youtube.com/channel/UCo2EEpr00LcG6el9BFgHkCA"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://www.wikus.de/typo3conf/ext/rb_template/Resources/Public/Media/Icons/YouTube.svg"
                  alt="You Tube"
                  title="You Tube"
                />
              </a>
              <a
                href="https://www.linkedin.com/company/wikus-gmbh-&amp;-co-kg"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://www.wikus.de/fileadmin/content/Images/linkedin.svg"
                  alt="LinkedIn"
                  title="LinkedIn"
                />
              </a>
            </div>
            <div>
              <img
                width="162px"
                src="assets/LRQA-2022-UKAS-ISO-9001-50001.jpg"
                alt="Quality Assurance"
                title="Quality Assurance"
              />
            </div>
          </div>
        </div>

        <div class="footer__links container">
          <div class="footer__links--content">
            <div class="left">
              <a href="https://www.wikus.de/de/impressum" target="_blank"
                >${translate('webapp.link.impressum')}</a
              >
              <a href="/datenschutz">${translate('privacy.title')}</a>
              <a href="/nutzungsbedingungen">${translate('terms.title')}</a>
            </div>

            <div class="right">&copy; WIKUS ${new Date().getFullYear()}</div>
          </div>
        </div>
      </footer>
    `;
  }

  renderLocaleLink(lang: string) {
    return html`
      <a
        class="dialog-locale-locales-link"
        @click="${async (e: MouseEvent) => {
          e.preventDefault();
          this.dialogLocaleVisible = false;
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.set('lang', lang);
          (window.location.search as any) = urlParams;
          // await store.dispatch(<any>setLang(lang));
        }}"
      >
        <span class="dialog-locale-locales-flag">
          <img src="${getFlagForLang(lang)}" />
        </span>
        <span class="dialog-locale-locales-label">
          ${this.renderLangugageName(lang)}
        </span>
      </a>
    `;
  }

  renderLangugageName(lang: string) {
    let parts;
    if (lang.match(/-/)) {
      parts = lang.split(/-/);
    } else if (lang.match(/_/)) {
      parts = lang.split(/_/);
    } else {
      return html`${translate(`kode4.lang.native.${lang}`)}`;
    }

    return html`
      ${translate(`kode4.lang.native.${parts[0]}`)}
      (${translate(`kode4.country.${parts[1]}`)})
    `;
  }

  createRenderRoot() {
    return this;
  }
}
